import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  MoreVert,
  CheckCircle,
  Visibility,
  Policy,
  LocalPolice,
  Star,
  StarBorder,
  RequestQuote,
} from "@mui/icons-material";
import { useAuth } from "../../context/context";
import { useNavigate } from "react-router-dom";
import { API_CONFIG } from "../../config/environment";
import PriceDetailsModal from "../forms/PriceDetailsModal";

const ProductMenuButton = ({
  product,
  onRegisterSale,
  externalSetSnackbarMessage,
  externalSetSnackbarOpen,
  externakSetSnackbarSeverity, // Mantener el typo para compatibilidad
}) => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);

  // 🎯 Snackbar interno (solo se usa si no hay externos)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const open = Boolean(anchorEl);

  // Solo mostrar para admins de BIENES
  const isAdminBienes = isAdmin() && user?.areaId === 1;

  if (!isAdminBienes) {
    return null;
  }

  const handleClick = (e) => {
    e.stopPropagation(); // Evitar que se active el click del card
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleRegisterSale = (e) => {
    e.stopPropagation();
    handleClose(e);
    if (onRegisterSale) {
      onRegisterSale(product);
    }
  };

  const handleViewSale = (e) => {
    e.stopPropagation();
    handleClose(e);

    // Construir URL con parámetros: tab=1 (Ventas Directas) y search=nombre_producto
    const searchQuery = encodeURIComponent(
      product.name ||
        product.category + " " + (product.marca || product.brand || "")
    );
    navigate(`/admin?tab=1&search=${searchQuery}`);
  };

  const handleOpenPriceDetails = (e) => {
    e.stopPropagation();
    handleClose(e);
    setPriceModalOpen(true);
  };

  const handleToggleFeatured = async (e) => {
    e.stopPropagation();
    handleClose(e);
    setLoading(true);

    try {
      const token = localStorage.getItem("tattersall-token");

      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      // Llamar al endpoint de featured_lots
      const response = await fetch(
        `${API_CONFIG.V1_BASE_URL}/featured_lots/${product.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || "Error al actualizar el producto");
      }

      // Consumir la respuesta (no necesitamos los datos)
      await response.json();

      // Determinar el nuevo estado del featured
      const newFeaturedState = !product.featured;

      // 🗑️ LIMPIAR TODO EL CACHÉ DE SESSIONSTORAGE
      try {
        console.log("🧹 Limpiando caché de sessionStorage...");

        // Limpiar caché de productos del catálogo
        sessionStorage.removeItem("tattersall-products-cache");
        sessionStorage.removeItem("tattersall-cache-metadata");

        // Limpiar caché de productos destacados del home
        sessionStorage.removeItem("tattersall-home-products-cache");
        sessionStorage.removeItem("tattersall-home-cache-metadata");

        // Limpiar otros datos de navegación si existen
        sessionStorage.removeItem("tattersall-scroll-position");
        sessionStorage.removeItem("home-navigation-filters");

        console.log("✅ Caché limpiado exitosamente");
      } catch (cacheError) {
        console.warn("⚠️ Error limpiando caché:", cacheError);
      }

      // 🎯 Usar snackbar externo si está disponible, sino usar el interno
      const message = newFeaturedState
        ? "⭐ Producto marcado como destacado"
        : "Producto desmarcado como destacado";

      if (externalSetSnackbarMessage) {
        externalSetSnackbarMessage(message);
        externalSetSnackbarOpen && externalSetSnackbarOpen(true);
        externakSetSnackbarSeverity && externakSetSnackbarSeverity("success");
      } else {
        setSnackbarMessage(message);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }

      // Recargar la página para reflejar los cambios
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error al actualizar featured:", error);

      // 🎯 Usar snackbar externo si está disponible, sino usar el interno
      const errorMessage = `❌ Error: ${error.message}`;

      if (externalSetSnackbarMessage) {
        externalSetSnackbarMessage(errorMessage);
        externalSetSnackbarOpen && externalSetSnackbarOpen(true);
        externakSetSnackbarSeverity && externakSetSnackbarSeverity("error");
      } else {
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={loading}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 100,
          backgroundColor: "rgb(255, 217, 0)",
          backdropFilter: "blur(4px)",
          border: "2px solid rgb(255, 255, 255)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          color: "white",
          "&:hover": {
            backgroundColor: "rgb(253, 236, 0)",
            border: "2px solid rgb(255, 255, 255)",
            transform: "scale(1.1)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {loading ? (
          <CircularProgress size={18} sx={{ color: "black" }} />
        ) : (
          <LocalPolice sx={{ color: "black", fontSize: "18px" }} />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock
      >
        {product.is_sold ? (
          <MenuItem onClick={handleViewSale}>
            <ListItemIcon>
              <Visibility fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Ver Venta en Backoffice</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleRegisterSale}>
            <ListItemIcon>
              <CheckCircle fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Registrar Venta</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleToggleFeatured}>
          <ListItemIcon>
            {product.featured ? (
              <Star fontSize="small" sx={{ color: "#ffd700" }} />
            ) : (
              <StarBorder fontSize="small" color="action" />
            )}
          </ListItemIcon>
          <ListItemText>
            {product.featured ? "Desmarcar Destacado" : "Marcar como Destacado"}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenPriceDetails}>
          <ListItemIcon>
            <RequestQuote fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Detalle de Precio</ListItemText>
        </MenuItem>
      </Menu>

      {/* 🎯 Snackbar interno (solo se muestra si NO hay snackbar externo) */}
      {!externalSetSnackbarMessage && (
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
      {/* 💰 Modal full-screen de Detalle de Precio */}
      <PriceDetailsModal
        open={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductMenuButton;
